import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonComponent } from '../button/button';
import { AuditTimelineComponent } from '../audit-timeline/audit-timeline';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal';
import { DetailPanelComponent } from '../detail-panel/detail-panel';
import { EditablePanelTitleComponent } from '../editable-panel-title/editable-panel-title';
import { PanelTabsComponent, PanelTab } from '../panel-tabs/panel-tabs';
import { SectionIconComponent } from '../section-icon/section-icon';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import { VendorMemberPicker } from '../vendor-member-picker/vendor-member-picker';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { StateStoreService } from '../../services/state-store/state-store';
import { VendorApi } from '../../services/vendor-api/vendor-api';
import type { AuditRecord, VendorList, VendorListMemberEntry } from '../../models';
import { FeatureId } from '../../constants/features.constants';
import { createWriteActionGate } from '../../shared/write-action-gate';

type ListTabId = 'details' | 'vendors' | 'history';

/**
 * Edit drawer for a single vendor list.
 *
 * Wraps the shared `<app-detail-panel>` (the resizable side drawer used by
 * every other detail surface in the app — controls, frameworks, documents,
 * tasks, assessments). The drawer is the canonical edit affordance for the
 * vendor-lists feature; the parent route opens it on card-click and never
 * surfaces inline Edit/Archive buttons on the card itself.
 *
 * Shape (mirrors the inventory-detail page):
 *   - Header: inline-editable title (Notion-style — input that looks like
 *     a heading until focused) + manager action row (Archive)
 *   - `<app-panel-tabs>` to switch between Details / Vendors / History
 *   - Persistent-form footer that appears when dirty
 *
 * Close reactivity: emitting `panelClose` is gated on `isDirty()`. When
 * dirty, the X / Escape / backdrop-click route through a "Discard
 * unsaved changes?" confirm so the user can't drop work by accident
 * (industry-standard guard; same as the document-editor flow uses for
 * navigation).
 *
 * Save model and BE wiring are unchanged from the previous flat-section
 * version: name + description go through `updateVendorList`, member
 * changes go through `replaceVendorListMembers`, and the two calls are
 * sequenced when both are dirty.
 */
@Component({
  selector: 'app-vendor-list-detail-panel',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    TranslocoModule,
    AuditTimelineComponent,
    ButtonComponent,
    ConfirmModalComponent,
    DetailPanelComponent,
    EditablePanelTitleComponent,
    PanelTabsComponent,
    SectionIconComponent,
    StatusIndicatorComponent,
    VendorMemberPicker,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './vendor-list-detail-panel.html',
  styleUrl: './vendor-list-detail-panel.css',
})
export class VendorListDetailPanel implements OnInit {
  private readonly api = inject(OrgManagementApi);
  private readonly stateStore = inject(StateStoreService);
  private readonly vendorApi = inject(VendorApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly translationVersion = signal(0);

  /** The list being edited. Required; the caller opens this drawer only
   *  when a list has been selected, never with null. */
  readonly list = input.required<VendorList>();
  /** Owning org id — needed for every BE call. */
  readonly orgId = input.required<string>();
  /** Whether the current user can edit (manager or admin). Drives the
   *  Archive action visibility and read-only-ness on inputs. Caller
   *  resolves the role so the drawer stays role-agnostic. */
  readonly canEdit = input<boolean>(true);

  // Subscription + feature entitlement gate. Layered on top of the
  // caller-resolved role check: an authority-eligible user whose org has
  // lost the plan/feature sees the write chrome (buttons, inputs) but
  // pre-emptively disabled with a tooltip explaining why. Role wins
  // precedence — a non-editor sees no chrome at all (as before), no
  // tooltip needed.
  private readonly actionGate = createWriteActionGate({
    stateStore: this.stateStore,
    feature: {
      id: FeatureId.MANAGED_VENDOR_DUE_DILIGENCE,
      reasonKey: 'disabled_tooltips.feature_not_entitled_vendor_lists',
    },
    // Mode-axis gate — see the parent list route for the rationale.
    requireFeatureWrite: {
      id: FeatureId.MANAGED_VENDOR_DUE_DILIGENCE,
      reasonKey: 'disabled_tooltips.feature_read_only_vendor_lists',
    },
  });
  protected readonly writesBlocked = this.actionGate.isBlocked;
  protected readonly writesBlockedReason = this.actionGate.disabledReason;

  /** True only when the caller has authority AND the org is entitled.
   *  Drives every read-only / disabled predicate in the template so
   *  they stay in sync — a single source of truth for "can this user
   *  mutate the list right now?". */
  protected readonly canMutate = computed(() => this.canEdit() && !this.writesBlocked());

  readonly panelClose = output<void>();
  readonly saved = output<void>();
  /** Emitted after a successful archive so the parent can refresh its
   *  list (the BE flips `archived_at`) and close the drawer. */
  readonly archived = output<void>();

  // Picker inputs come from the same store the modal reads from.
  protected readonly inventoryVendors = this.stateStore.vendors;
  protected readonly registry = this.stateStore.globalVendorRegistry;

  protected readonly widthPx = signal(560);

  protected readonly activeTab = signal<ListTabId>('details');
  protected readonly tabs = computed<PanelTab[]>(() => {
    this.translationVersion();
    const t = (k: string) => this.transloco.translate<string>(k);
    return [
      { id: 'details', label: t('panel_vendor_list.tab_details') },
      {
        id: 'vendors',
        label: t('panel_vendor_list.tab_vendors'),
        count: this.selectedMembers().size,
      },
      { id: 'history', label: t('panel_vendor_list.tab_history') },
    ];
  });

  // ─── Form state ─────────────────────────────────────────────────────────
  protected readonly name = signal('');
  protected readonly description = signal('');
  protected readonly selectedMembers = signal<Map<string, string[]>>(new Map());

  /** Captured loaded snapshot — used to derive `isDirty`. Set in ngOnInit
   *  after hydrating from the BE so the initial render isn't flagged as
   *  dirty against an empty baseline. */
  private readonly loadedName = signal('');
  private readonly loadedDescription = signal('');
  private readonly loadedMembers = signal<Map<string, string[]>>(new Map());

  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly membersLoading = signal(true);
  protected readonly showArchiveConfirm = signal(false);
  protected readonly showDiscardConfirm = signal(false);

  // ─── History (audit events) ────────────────────────────────────────────
  protected readonly auditEvents = signal<AuditRecord[]>([]);
  protected readonly historyLoading = signal(false);
  /** Tracks whether the history tab has been loaded at least once so we
   *  don't refetch every time the user clicks back to the tab. */
  private historyLoaded = false;

  protected readonly isDirty = computed(() => {
    if (this.name().trim() !== this.loadedName()) return true;
    if ((this.description().trim() || '') !== this.loadedDescription()) return true;
    return !sameSelection(this.selectedMembers(), this.loadedMembers());
  });

  protected readonly canSave = computed(
    () => !this.busy() && this.name().trim().length > 0 && this.isDirty()
  );

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    // Lazy-load audit history on first tab activation. Idempotent — the
    // tab can be re-entered as many times as the user wants without
    // hammering the API.
    effect(() => {
      if (this.activeTab() === 'history' && !this.historyLoaded) {
        this.loadHistory();
      }
    });
  }

  ngOnInit(): void {
    const l = this.list();
    this.name.set(l.name);
    this.description.set(l.description ?? '');
    this.loadedName.set(l.name);
    this.loadedDescription.set(l.description ?? '');
    this.hydrateMembers(l.list_id);
  }

  // ─── Close flow ────────────────────────────────────────────────────────
  // Dirty-aware close — emits `panelClose` immediately when clean, raises
  // a "Discard unsaved changes?" confirm when dirty. Mirrors the guard
  // used by every other persistent-form surface in the app.

  protected onClose(): void {
    if (this.isDirty()) {
      this.showDiscardConfirm.set(true);
      return;
    }
    this.panelClose.emit();
  }

  protected confirmDiscard(): void {
    this.showDiscardConfirm.set(false);
    // Revert ensures the next ngOnInit (if this component is reused via
    // a different list input) starts from a clean baseline. The parent
    // is about to unmount the drawer anyway so this is belt-and-braces.
    this.onRevert();
    this.panelClose.emit();
  }

  protected cancelDiscard(): void {
    this.showDiscardConfirm.set(false);
  }

  protected onTabChange(id: ListTabId): void {
    this.activeTab.set(id);
  }

  // ─── Save flow ─────────────────────────────────────────────────────────

  protected onSave(): void {
    if (!this.canSave() || this.writesBlocked()) return;
    this.busy.set(true);
    this.error.set(null);

    const detailsChanged =
      this.name().trim() !== this.loadedName() ||
      (this.description().trim() || '') !== this.loadedDescription();
    const membersChanged = !sameSelection(this.selectedMembers(), this.loadedMembers());

    const replaceMembers$ = membersChanged
      ? this.stateStore.replaceVendorListMembers(
          this.list().list_id,
          this.buildMemberEntries(),
          this.orgId()
        )
      : null;

    const writeDetails$ = detailsChanged
      ? this.stateStore.updateVendorList(
          this.list().list_id,
          {
            name: this.name().trim(),
            description: this.description().trim() || undefined,
          },
          this.orgId()
        )
      : null;

    const finalize = (): void => {
      this.busy.set(false);
      // Snap the baselines forward so the form re-renders as clean
      // without closing the drawer — UX matches the dossier
      // persistent-form pattern.
      this.loadedName.set(this.name().trim());
      this.loadedDescription.set(this.description().trim() || '');
      this.loadedMembers.set(snapshotSelection(this.selectedMembers()));
      // Saving produces a new audit row; invalidate the cache so the
      // History tab refreshes on next visit (or now if already open).
      this.historyLoaded = false;
      if (this.activeTab() === 'history') this.loadHistory();
      this.saved.emit();
    };

    if (writeDetails$ && replaceMembers$) {
      writeDetails$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () =>
          replaceMembers$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: finalize,
            error: (err: Error) => this.onError(err),
          }),
        error: (err: Error) => this.onError(err),
      });
      return;
    }

    const only$ = writeDetails$ ?? replaceMembers$;
    if (!only$) {
      // Nothing changed — caller shouldn't have been able to click
      // Save in that state; treat as a no-op.
      this.busy.set(false);
      return;
    }
    only$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: finalize,
      error: (err: Error) => this.onError(err),
    });
  }

  protected onRevert(): void {
    if (this.busy()) return;
    this.name.set(this.loadedName());
    this.description.set(this.loadedDescription());
    this.selectedMembers.set(snapshotSelection(this.loadedMembers()));
    this.error.set(null);
  }

  // ─── Archive flow ──────────────────────────────────────────────────────
  // Lives in the drawer (not the parent route) so the action is invoked
  // from inside the surface the user is editing, mirroring how Offboard
  // sits on the per-relationship inventory-detail header.

  protected requestArchive(): void {
    if (!this.canMutate() || this.list().archived_at) return;
    this.showArchiveConfirm.set(true);
  }

  protected cancelArchive(): void {
    if (this.busy()) return;
    this.showArchiveConfirm.set(false);
  }

  protected confirmArchive(): void {
    if (!this.canMutate() || this.list().archived_at) return;
    this.busy.set(true);
    this.error.set(null);
    this.stateStore
      .archiveVendorList(this.list().list_id, this.orgId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.showArchiveConfirm.set(false);
          this.archived.emit();
        },
        error: (err: Error) => {
          this.showArchiveConfirm.set(false);
          this.onError(err);
        },
      });
  }

  protected dismissError(): void {
    this.error.set(null);
  }

  // ─── Data ──────────────────────────────────────────────────────────────

  private hydrateMembers(listId: string): void {
    this.membersLoading.set(true);
    this.vendorApi
      .getVendorList(this.orgId(), listId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          const members = res.members ?? [];
          const map = new Map<string, string[]>();
          for (const m of members) map.set(m.vendor_id, m.product_ids ?? []);
          // Legacy fallback: pre-products lists only have vendor_ids on
          // the top level. Treat each as "all products" so the load
          // doesn't drop members.
          if (map.size === 0 && res.vendor_ids?.length) {
            for (const vid of res.vendor_ids) map.set(vid, []);
          }
          this.selectedMembers.set(map);
          this.loadedMembers.set(snapshotSelection(map));
          this.membersLoading.set(false);
        },
        error: () => this.membersLoading.set(false),
      });
  }

  private loadHistory(): void {
    if (this.historyLoading()) return;
    this.historyLoading.set(true);
    this.api
      .getAuditTrail(this.orgId(), 'vendor_list', this.list().list_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: events => {
          this.auditEvents.set(events);
          this.historyLoading.set(false);
          this.historyLoaded = true;
        },
        error: () => {
          this.auditEvents.set([]);
          this.historyLoading.set(false);
          this.historyLoaded = true;
        },
      });
  }

  private buildMemberEntries(): VendorListMemberEntry[] {
    const entries: VendorListMemberEntry[] = [];
    for (const [vendor_id, product_ids] of this.selectedMembers()) {
      entries.push({ vendor_id, product_ids });
    }
    return entries;
  }

  private onError(err: Error): void {
    this.busy.set(false);
    this.error.set(err.message || 'Something went wrong');
  }
}

/** Structural equality for the selection map — same vendors picked,
 *  same per-vendor product ids in the same order. Used for `isDirty`
 *  and `onRevert` baseline restoration. */
function sameSelection(a: Map<string, string[]>, b: Map<string, string[]>): boolean {
  if (a.size !== b.size) return false;
  for (const [k, va] of a) {
    const vb = b.get(k);
    if (!vb || vb.length !== va.length) return false;
    for (let i = 0; i < va.length; i++) {
      if (va[i] !== vb[i]) return false;
    }
  }
  return true;
}

/** Defensive copy. Selection maps are mutated through `signal.update`,
 *  but the baseline reference must not change behind our back. */
function snapshotSelection(src: Map<string, string[]>): Map<string, string[]> {
  const copy = new Map<string, string[]>();
  for (const [k, v] of src) copy.set(k, [...v]);
  return copy;
}
